'use client';

import { type CSSProperties, useEffect } from 'react';

const diagnosticRows = [
  ['EDGE', '链路重试待命'],
  ['CACHE', '会话状态保留'],
  ['CORE', '诊断信号已记录'],
];

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    if (
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Failed to fetch dynamically imported module')
    ) {
      const reloadKey = `chunk_reload_${window.location.pathname}`;
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();

      if (!lastReload || now - parseInt(lastReload) > 10000) {
        sessionStorage.setItem(reloadKey, now.toString());
        window.location.reload();
        return;
      }
    }
  }, [error]);

  // 提取错误码（如果有），否则使用通用错误码
  const errorCodeMatch = error.message?.match(/E(\d{4})/);
  const errorCode = errorCodeMatch ? errorCodeMatch[0] : 'E1005';

  return (
    <section
      role="alert"
      className="fx-error-shell relative isolate min-h-[calc(100vh-60px)] overflow-hidden bg-bg-primary px-5 py-10 sm:px-8 lg:px-14"
    >
      <div className="fx-noise-layer" aria-hidden="true" />
      <div className="fx-scanline" aria-hidden="true" />
      <div className="fx-amber-vignette" aria-hidden="true" />

      <div className="relative z-10 mx-auto grid min-h-[calc(100vh-140px)] w-full max-w-7xl items-center gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(340px,0.72fr)]">
        <div className="flex min-w-0 flex-col justify-end pt-12 lg:min-h-[520px]">
          <p className="fx-reveal mb-5 font-mono text-xs text-[#ffb547]/80 [--delay:90ms]">
            SIGNAL DIAGNOSTIC / MAIN SITE
          </p>

          <h1
            className="fx-code fx-reveal font-mono text-[clamp(5.5rem,20vw,17rem)] leading-[0.78] text-[#ffb547] [--delay:150ms]"
            data-code={errorCode}
            aria-label={`错误码 ${errorCode}`}
          >
            {errorCode}
          </h1>

          <div className="fx-reveal mt-8 max-w-2xl border-l border-[#ffb547]/45 pl-5 [--delay:260ms] sm:pl-7">
            <p className="font-mono text-xs text-text-tertiary">
              CONNECTION STATE: INTERRUPTED
            </p>
            <h2 className="mt-3 text-2xl font-medium text-text-primary sm:text-3xl">
              页面加载出现问题
            </h2>
            <p className="mt-4 max-w-xl text-base leading-7 text-text-secondary">
              服务异常，请稍后重试。如问题持续，请联系管理员。
            </p>
          </div>
        </div>

        <div className="fx-reveal flex flex-col gap-4 border border-border bg-bg-secondary/65 p-5 shadow-[0_24px_80px_rgba(0,0,0,0.45)] backdrop-blur-md [--delay:380ms] sm:p-6">
          <div className="flex items-center justify-between gap-4 border-b border-border pb-4">
            <div>
              <p className="font-mono text-xs text-[#ffb547]">SYSTEM NOTE</p>
              <p className="mt-1 text-sm text-text-secondary">诊断终端</p>
            </div>
            <div className="h-2.5 w-2.5 rounded-full bg-[#ffb547] shadow-[0_0_18px_rgba(255,181,71,0.85)]" />
          </div>

          <div className="grid gap-3">
            {diagnosticRows.map(([label, value], index) => (
              <div
                key={label}
                className="fx-reveal flex items-center justify-between gap-4 border-b border-divider pb-3 last:border-b-0 last:pb-0"
                style={{ '--delay': `${470 + index * 90}ms` } as CSSProperties}
              >
                <span className="font-mono text-xs text-text-tertiary">{label}</span>
                <span className="text-right text-sm text-text-secondary">{value}</span>
              </div>
            ))}
          </div>

          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <button
              onClick={() => window.location.reload()}
              className="inline-flex h-11 items-center justify-center border border-[#ffb547]/60 bg-[#ffb547] px-5 text-sm font-medium text-black transition hover:bg-[#ffc36d] focus:outline-none focus:ring-2 focus:ring-[#ffb547]/50"
            >
              刷新页面
            </button>
            <button
              onClick={reset}
              className="inline-flex h-11 items-center justify-center border border-border bg-transparent px-5 text-sm font-medium text-text-primary transition hover:border-[#ffb547]/50 hover:bg-[#ffb547]/10 focus:outline-none focus:ring-2 focus:ring-[#ffb547]/35"
            >
              重试
            </button>
          </div>
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        .fx-error-shell::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 0;
          background-image:
            linear-gradient(rgba(255, 181, 71, 0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 181, 71, 0.06) 1px, transparent 1px);
          background-size: 96px 96px;
          mask-image: radial-gradient(circle at 24% 44%, black 0%, transparent 68%);
          opacity: 0.45;
        }

        .fx-noise-layer {
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          background-image:
            radial-gradient(circle at 10% 20%, rgba(255, 255, 255, 0.12) 0 1px, transparent 1px),
            radial-gradient(circle at 70% 60%, rgba(255, 255, 255, 0.08) 0 1px, transparent 1px),
            radial-gradient(circle at 35% 85%, rgba(255, 181, 71, 0.12) 0 1px, transparent 1px);
          background-size: 17px 17px, 23px 23px, 29px 29px;
          mix-blend-mode: screen;
          opacity: 0.22;
        }

        .fx-scanline {
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          background: linear-gradient(
            to bottom,
            transparent 0%,
            rgba(255, 181, 71, 0.12) 49%,
            rgba(255, 181, 71, 0.2) 50%,
            transparent 52%,
            transparent 100%
          );
          background-size: 100% 10px;
          opacity: 0.24;
          animation: fxScan 8s linear infinite;
        }

        .fx-amber-vignette {
          position: absolute;
          inset: -18%;
          z-index: 0;
          pointer-events: none;
          background:
            radial-gradient(circle at 18% 28%, rgba(255, 181, 71, 0.2), transparent 28%),
            radial-gradient(circle at 88% 74%, rgba(254, 67, 67, 0.1), transparent 30%),
            linear-gradient(120deg, rgba(255, 181, 71, 0.08), transparent 38%, rgba(255, 255, 255, 0.04));
          opacity: 0.72;
        }

        .fx-reveal {
          opacity: 0;
          transform: translateY(16px);
          animation: fxReveal 720ms cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
          animation-delay: var(--delay, 0ms);
        }

        .fx-code {
          position: relative;
          display: inline-block;
          text-shadow:
            0 0 20px rgba(255, 181, 71, 0.34),
            0 0 60px rgba(255, 181, 71, 0.16);
          animation:
            fxReveal 720ms cubic-bezier(0.2, 0.8, 0.2, 1) forwards,
            fxFlicker 5.8s steps(1, end) infinite;
          animation-delay: var(--delay, 0ms), 1.1s;
        }

        .fx-code::before,
        .fx-code::after {
          content: attr(data-code);
          position: absolute;
          inset: 0;
          pointer-events: none;
          opacity: 0;
        }

        .fx-code::before {
          color: rgba(255, 255, 255, 0.72);
          transform: translateX(-2px);
          clip-path: inset(0 0 62% 0);
          animation: fxGlitchA 6.4s steps(1, end) infinite;
        }

        .fx-code::after {
          color: rgba(254, 67, 67, 0.68);
          transform: translateX(2px);
          clip-path: inset(58% 0 0 0);
          animation: fxGlitchB 7.2s steps(1, end) infinite;
        }

        @keyframes fxReveal {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fxScan {
          from {
            transform: translateY(-10%);
          }
          to {
            transform: translateY(10%);
          }
        }

        @keyframes fxFlicker {
          0%, 100% {
            opacity: 1;
          }
          46% {
            opacity: 0.92;
          }
          47% {
            opacity: 1;
          }
          82% {
            opacity: 0.88;
          }
          83% {
            opacity: 1;
          }
        }

        @keyframes fxGlitchA {
          0%, 91%, 100% {
            opacity: 0;
            transform: translateX(-2px);
          }
          92% {
            opacity: 0.34;
            transform: translateX(-8px);
          }
          93% {
            opacity: 0.12;
            transform: translateX(3px);
          }
        }

        @keyframes fxGlitchB {
          0%, 76%, 100% {
            opacity: 0;
            transform: translateX(2px);
          }
          77% {
            opacity: 0.28;
            transform: translateX(7px);
          }
          78% {
            opacity: 0.1;
            transform: translateX(-3px);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .fx-reveal,
          .fx-code,
          .fx-code::before,
          .fx-code::after,
          .fx-scanline {
            animation: none !important;
          }

          .fx-reveal {
            opacity: 1;
            transform: none;
          }

          .fx-code::before,
          .fx-code::after {
            opacity: 0;
          }
        }
      `,
        }}
      />
    </section>
  );
}
